import React, {useEffect, useState} from 'react'
import {useThemeMode, systemMode} from '../../../_metronic/partials/layout/theme-mode/ThemeModeProvider'
import {genericGetAll} from '../generic/crud'
import {ENTITY_ID_WATER_WASH} from '../generic/Constants'

type Props = {
  // New: pre-computed values (Admin Dashboard)
  today?: number
  thisWeek?: number
  thisMonth?: number
  // Old: self-fetching mode (Manager/Supervisor/Finance)
  branchIds?: string[]
  className?: string
}

export const WaterWashCard: React.FC<Props> = ({today: todayProp, thisWeek: thisWeekProp, thisMonth: thisMonthProp, branchIds, className}) => {
  const {mode} = useThemeMode();
  const isDarkMode = mode === 'dark' || (mode === 'system' && systemMode === 'dark');

  // Self-fetching mode when pre-computed values are not provided
  const isPreComputed = todayProp !== undefined;
  const [fetchedStats, setFetchedStats] = useState({today: 0, thisWeek: 0, thisMonth: 0});
  const [fetchLoading, setFetchLoading] = useState(!isPreComputed);

  useEffect(() => {
    if (isPreComputed) return;
    setFetchLoading(true);
    genericGetAll(ENTITY_ID_WATER_WASH).then((res: any) => {
      const records = res?.data?.data || [];
      const now = new Date();
      const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const dayOfWeek = now.getDay();
      const startOfWeek = new Date(startOfToday);
      startOfWeek.setDate(startOfWeek.getDate() - dayOfWeek);
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

      let todayCount = 0, weekCount = 0, monthCount = 0;
      for (const r of records) {
        const d = new Date(r.createdOn);
        if (branchIds && branchIds.length > 0 && !branchIds.includes(r.branchId)) continue;
        if (d >= startOfMonth) monthCount++;
        if (d >= startOfWeek) weekCount++;
        if (d >= startOfToday) todayCount++;
      }
      setFetchedStats({today: todayCount, thisWeek: weekCount, thisMonth: monthCount});
    }).catch(() => {}).finally(() => setFetchLoading(false));
  }, [isPreComputed, branchIds]);

  const today = isPreComputed ? (todayProp || 0) : fetchedStats.today;
  const thisWeek = isPreComputed ? (thisWeekProp || 0) : fetchedStats.thisWeek;
  const thisMonth = isPreComputed ? (thisMonthProp || 0) : fetchedStats.thisMonth;

  return (
    <div
        className={`card card-flush ${className}`}
        style={{
            background: isDarkMode
                ? 'linear-gradient(112.14deg, #1B1B29 0%, #161621 100%)'
                : '#ffffff',
            boxShadow: isDarkMode ? 'none' : '0px 0px 20px 0px rgba(76, 87, 125, 0.02)'
        }}
    >
      <div className="card-body d-flex flex-column justify-content-between p-6">
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between mb-5">
            <div className="d-flex align-items-center">
                <div className="symbol symbol-45px symbol-circle me-3" style={{ background: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 158, 247, 0.1)' }}>
                    <div className="symbol-label" style={{ background: 'transparent' }}>
                        <i className={`bi bi-droplet-fill fs-2 ${isDarkMode ? 'text-white' : 'text-primary'}`}></i>
                    </div>
                </div>
                <div className="d-flex flex-column">
                    <span className={`${isDarkMode ? 'text-white' : 'text-gray-900'} fw-bold fs-4`}>Water Wash</span>
                    <span className={`${isDarkMode ? 'text-white' : 'text-gray-500'} opacity-75 fw-semibold fs-7`}>Live Activity</span>
                </div>
            </div>
        </div>

        {/* Main Stat - Today */}
        <div className="d-flex flex-column align-items-start mb-7">
            <span className={`${isDarkMode ? 'text-white' : 'text-gray-900'} fw-bold lh-1 ls-n2`} style={{ fontSize: '4rem' }}>
                {today}
            </span>
            <span className={`${isDarkMode ? 'text-white' : 'text-gray-500'} opacity-75 fw-semibold fs-6 mt-1`}>
                Completed Today
            </span>
        </div>

        {/* Footer Stats - Week & Month */}
        <div className="d-flex gap-4">
            <div className="d-flex align-items-center rounded p-3 flex-grow-1" style={{ background: isDarkMode ? 'rgba(255, 255, 255, 0.15)' : '#F9F9F9' }}>
                <i className={`bi bi-calendar-week fs-3 me-3 opacity-75 ${isDarkMode ? 'text-white' : 'text-primary'}`}></i>
                <div className="d-flex flex-column">
                    <span className={`${isDarkMode ? 'text-white' : 'text-gray-900'} fw-bold fs-5 lh-1`}>{thisWeek}</span>
                    <span className={`${isDarkMode ? 'text-white' : 'text-gray-500'} opacity-60 fs-8 fw-semibold mt-1`}>This Week</span>
                </div>
            </div>

            <div className="d-flex align-items-center rounded p-3 flex-grow-1" style={{ background: isDarkMode ? 'rgba(255, 255, 255, 0.15)' : '#F9F9F9' }}>
                <i className={`bi bi-calendar-month fs-3 me-3 opacity-75 ${isDarkMode ? 'text-white' : 'text-success'}`}></i>
                <div className="d-flex flex-column">
                    <span className={`${isDarkMode ? 'text-white' : 'text-gray-900'} fw-bold fs-5 lh-1`}>{thisMonth}</span>
                    <span className={`${isDarkMode ? 'text-white' : 'text-gray-500'} opacity-60 fs-8 fw-semibold mt-1`}>This Month</span>
                </div>
            </div>
        </div>
      </div>
    </div>
  )
}
